import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { toastHandler } from '@/components/utils/Toast'
import { useAuthContext } from '@/context'
import { useFinderContext } from '@/context/finder-context'
import { keyUser } from '@/services/auth/queries'
import { useCreateWorkspace } from '@/services/org/mutations'
import { ReactNode } from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog'

type FormValues = {
    name: string
}

type Props = {
    open: boolean
    onOpenChange: (open: boolean) => void
    /** Omit when the parent opens the dialog (e.g. DropdownMenuItem + controlled state). */
    trigger?: ReactNode
    onClose?: () => void
}

const CreateWorkspace = ({ trigger, open, onOpenChange }: Props) => {
    const navigate = useNavigate()
    const { setSelectedItems } = useFinderContext()
    const { org } = useAuthContext()
    const methods = useForm<FormValues>()

    const {
        control,
        formState: { isValid },
        handleSubmit,
    } = methods

    const { mutate: createWorkspace, isLoading: creating } = useCreateWorkspace({
        refetch: [keyUser],
        onSuccess: () => {
            toastHandler({ message: 'Workspace created' })
            onOpenChange(false)
            setSelectedItems([])
            navigate(`/dashboard`)
        },
    })

    const onSubmit = (values: any) => {
        const payload = {
            name: values.name,
            email: org?.email,
        }
        createWorkspace(payload)
    }

    const loading = creating

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            {trigger != null ? (
                <DialogTrigger asChild className='w-full'>
                    {trigger}
                </DialogTrigger>
            ) : null}
            <DialogContent className='max-w-[600px]'>
                <DialogHeader>
                    <DialogTitle>Create New Workspace</DialogTitle>
                </DialogHeader>
                <FormProvider {...methods}>
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <div>
                            <div className='grid gap-4 py-4'>
                                <div className='flex flex-col gap-2'>
                                    <Label htmlFor='title'>Workspace name *</Label>

                                    <Controller
                                        shouldUnregister
                                        name='name'
                                        rules={{
                                            required: true,
                                        }}
                                        control={control}
                                        render={({ field }) => <Input {...field} className='mb-4' />}
                                    />
                                </div>
                            </div>
                        </div>
                        <DialogFooter>
                            <div className='flex justify-end gap-4'>
                                <Button variant='outline' type='button' onClick={() => onOpenChange(false)}>
                                    Cancel
                                </Button>
                                <Button disabled={!isValid} type='submit' loading={loading}>
                                    Create Workspace
                                </Button>
                            </div>
                        </DialogFooter>
                    </form>
                </FormProvider>
            </DialogContent>
        </Dialog>
    )
}

export default CreateWorkspace
